import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { CarOptionSet } from "@/types/database";
import { useCreateCarOptionSet, useUpdateCarOptionSet } from "@/lib/queries";
import {
  defaultOptionSetForm,
  optionSetToForm,
  parseOptionSetList,
  type OptionSetFormState,
} from "@/lib/option-set-form";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Input";
import { cn } from "@/lib/cn";

const STEPS = ["Make", "Models", "Trims", "Review"] as const;

export function OptionSetWizard({
  open,
  onClose,
  optionSet,
}: {
  open: boolean;
  onClose: () => void;
  optionSet?: CarOptionSet | null;
}) {
  const createOptionSet = useCreateCarOptionSet();
  const updateOptionSet = useUpdateCarOptionSet();
  const isEditing = Boolean(optionSet);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<OptionSetFormState>(defaultOptionSetForm);
  const [error, setError] = useState<string | null>(null);

  const models = parseOptionSetList(form.models);
  const trims = parseOptionSetList(form.trims);
  const saving = createOptionSet.isPending || updateOptionSet.isPending;

  function resetWizard() {
    setStep(0);
    setForm(defaultOptionSetForm());
    setError(null);
  }

  function closeWizard() {
    if (saving) return;
    resetWizard();
    onClose();
  }

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError(null);
    setForm(optionSet ? optionSetToForm(optionSet) : defaultOptionSetForm());
  }, [open, optionSet]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) closeWizard();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, saving, onClose]);

  function canContinue(): boolean {
    if (step === 0) return form.make.trim().length > 0;
    if (step === 1) return models.length > 0;
    return true;
  }

  async function handleSave() {
    setError(null);
    const payload = {
      make: form.make.trim(),
      models,
      trims,
    };

    try {
      if (optionSet) {
        await updateOptionSet.mutateAsync({ id: optionSet.id, updates: payload });
      } else {
        await createOptionSet.mutateAsync(payload);
      }
      closeWizard();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save option set");
    }
  }

  function handleNext() {
    if (!canContinue()) return;
    if (step < STEPS.length - 1) {
      setStep((current) => current + 1);
      return;
    }
    void handleSave();
  }

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[100] flex h-dvh min-h-dvh flex-col bg-background"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <header className="flex items-center justify-between gap-4 border-b border-border/60 px-6 py-4 sm:px-8">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted">
                Step {step + 1} of {STEPS.length}
              </p>
              <h2 className="text-xl font-bold tracking-tight text-foreground">
                {isEditing ? "Edit option set" : "New option set"}
              </h2>
            </div>
            <button
              type="button"
              aria-label="Close wizard"
              onClick={closeWizard}
              disabled={saving}
              className="flex h-10 w-10 items-center justify-center rounded-full text-muted transition hover:bg-canvas hover:text-foreground focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="border-b border-border/40 px-6 py-4 sm:px-8">
            <div className="flex flex-wrap gap-2">
              {STEPS.map((label, index) => (
                <span
                  key={label}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-bold transition-colors",
                    index === step
                      ? "bg-accent text-accent-foreground"
                      : index < step
                        ? "bg-accent-subtle text-accent-foreground"
                        : "bg-canvas text-muted"
                  )}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          <main className="flex-1 overflow-y-auto px-6 py-8 sm:px-8">
            <div className="mx-auto w-full max-w-xl">
              {error ? (
                <p className="mb-6 rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
              ) : null}

              {step === 0 ? (
                <div className="space-y-2">
                  <h3 className="text-lg font-bold">Vehicle make</h3>
                  <p className="text-sm text-muted">
                    Enter the manufacturer name for this catalogue entry. Each option set represents one make.
                  </p>
                  <div className="mt-6">
                    <Field label="Make">
                      <Input
                        placeholder="e.g. Mercedes-Benz"
                        value={form.make}
                        onChange={(e) => setForm((current) => ({ ...current, make: e.target.value }))}
                        autoFocus
                      />
                    </Field>
                  </div>
                </div>
              ) : null}

              {step === 1 ? (
                <div className="space-y-2">
                  <h3 className="text-lg font-bold">Models</h3>
                  <p className="text-sm text-muted">Add one model per line or separate with commas.</p>
                  <div className="mt-6">
                    <Field label="Models">
                      <textarea
                        className="field-input min-h-[180px] resize-y"
                        placeholder={"GLE450\nC-Class\nE-Class"}
                        value={form.models}
                        onChange={(e) => setForm((current) => ({ ...current, models: e.target.value }))}
                        autoFocus
                      />
                    </Field>
                    {models.length > 0 ? (
                      <p className="mt-2 text-xs font-semibold text-muted">{models.length} models added</p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {step === 2 ? (
                <div className="space-y-2">
                  <h3 className="text-lg font-bold">Trims</h3>
                  <p className="text-sm text-muted">Optional. Add trim levels users can choose in Shift.</p>
                  <div className="mt-6">
                    <Field label="Trims">
                      <textarea
                        className="field-input min-h-[180px] resize-y"
                        placeholder={"4MATIC\nAMG Line\nBase"}
                        value={form.trims}
                        onChange={(e) => setForm((current) => ({ ...current, trims: e.target.value }))}
                        autoFocus
                      />
                    </Field>
                    {trims.length > 0 ? (
                      <p className="mt-2 text-xs font-semibold text-muted">{trims.length} trims added</p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {step === 3 ? (
                <div className="space-y-2">
                  <h3 className="text-lg font-bold">Review</h3>
                  <p className="text-sm text-muted">Confirm the catalogue entry before saving to Supabase.</p>
                  <div className="mt-6 space-y-4 rounded-3xl bg-canvas p-5">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-muted">Make</p>
                      <p className="mt-1 font-bold">{form.make.trim()}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-muted">Models</p>
                      <p className="mt-1 text-sm">{models.join(", ") || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-muted">Trims</p>
                      <p className="mt-1 text-sm">{trims.length > 0 ? trims.join(", ") : "None"}</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </main>

          <footer className="mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-border/60 px-6 py-4 sm:px-8">
            <Button variant="ghost" onClick={closeWizard} disabled={saving}>
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => setStep((current) => current - 1)}
                disabled={step === 0 || saving}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button variant="brand" onClick={handleNext} disabled={!canContinue() || saving}>
                {step === STEPS.length - 1 ? (
                  saving ? "Saving…" : isEditing ? "Save changes" : "Create option set"
                ) : (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </footer>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
